/**
 * Utility functions for blog-related operations
 */

/**
 * Format date string to readable format
 * @param {string} dateString - Date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

/**
 * Extract category names from categories array
 * @param {Array} categories - Array of category objects
 * @returns {Array} Array of category names
 */
export const getCategoryNames = (categories) => {
    if (!categories || categories.length === 0) return ['Uncategorized'];
    const categoryNames = categories.map(cat => cat.name).filter(name => name);
    return categoryNames.length > 0 ? categoryNames : ['Uncategorized'];
};

/**
 * Generate table of contents from HTML content
 * @param {string} htmlContent - HTML content string
 * @returns {Array} Array of TOC items with id, text, and element
 */
export const generateTableOfContents = (htmlContent) => {
    if (!htmlContent) return [];
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const headings = tempDiv.querySelectorAll('h2');
    
    return Array.from(headings).map((heading, index) => {
        const id = `heading-${index}`;
        heading.id = id;
        return {
            id,
            text: heading.textContent.trim(),
            element: heading
        };
    });
};

/**
 * Add IDs to headings in the rendered content
 * @param {Array} tableOfContents - Array of TOC items
 * @param {string} contentSelector - CSS selector for content container
 */
export const addHeadingIds = (tableOfContents, contentSelector) => {
    if (tableOfContents.length === 0) return;
    
    const blogContent = document.querySelector(contentSelector);
    if (blogContent) {
        const headings = blogContent.querySelectorAll('h2');
        headings.forEach((heading, index) => {
            if (index < tableOfContents.length) {
                heading.id = tableOfContents[index].id;
            }
        });
    }
};

/**
 * Wrap tables in responsive containers
 * @param {string} contentSelector - CSS selector for content container
 */
export const wrapTables = (contentSelector) => {
    const blogContent = document.querySelector(contentSelector);
    if (blogContent) {
        const tables = blogContent.querySelectorAll('table');
        tables.forEach(table => {
            if (!table.parentElement.classList.contains('table-wrapper')) {
                const wrapper = document.createElement('div');
                wrapper.className = 'table-wrapper';
                table.parentNode.insertBefore(wrapper, table);
                wrapper.appendChild(table);
            }
        });
    }
};

/**
 * Smooth scroll to heading
 * @param {string} headingId - ID of the heading to scroll to
 */
export const scrollToHeading = (headingId) => {
    const element = document.getElementById(headingId);
    if (element) {
        element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
        });
    }
};
